/**
 * Safety API Service
 * 
 * API client for SSA (System Safety Assessment) endpoints.
 * All routes are nested under /projects/{projectId}/safety-assessments
 */

import { api } from './api';
import type {
  SafetyAssessment,
  SFHAWorkProduct,
  FTAWorkProduct,
  FMEAWorkProduct,
  CMAWorkProduct,
  TraceabilitySummary,
  CreateSafetyAssessmentRequest,
  GenerateSSAReportRequest,
} from '@aerotage/shared';

// Helper to build the base path
const basePath = (projectId: string) => `/projects/${projectId}/safety-assessments`;

export const safetyApi = {
  // ==================== Safety Assessments ====================

  /**
   * List all safety assessments for a project
   */
  async listAssessments(projectId: string): Promise<SafetyAssessment[]> {
    const response = await api.get<{ data: SafetyAssessment[] }>(
      basePath(projectId)
    );
    return response.data.data || response.data as unknown as SafetyAssessment[];
  },

  /**
   * Get a safety assessment by ID
   */
  async getAssessment(projectId: string, assessmentId: string): Promise<SafetyAssessment> {
    const response = await api.get<{ data: SafetyAssessment }>(
      `${basePath(projectId)}/${assessmentId}`
    );
    return response.data.data || response.data as unknown as SafetyAssessment;
  },

  /**
   * Create a new safety assessment
   */
  async createAssessment(
    projectId: string,
    data: CreateSafetyAssessmentRequest
  ): Promise<SafetyAssessment> {
    const response = await api.post<{ data: SafetyAssessment }>(
      basePath(projectId),
      data
    );
    return response.data.data || response.data as unknown as SafetyAssessment;
  },

  /**
   * Update a safety assessment
   */
  async updateAssessment(
    projectId: string,
    assessmentId: string,
    data: Partial<SafetyAssessment>
  ): Promise<SafetyAssessment> {
    const response = await api.patch<{ data: SafetyAssessment }>(
      `${basePath(projectId)}/${assessmentId}`,
      data
    );
    return response.data.data || response.data as unknown as SafetyAssessment;
  },

  /**
   * Delete a safety assessment
   */
  async deleteAssessment(projectId: string, assessmentId: string): Promise<void> {
    await api.delete(`${basePath(projectId)}/${assessmentId}`);
  },

  /**
   * Advance assessment stage (PSSA → SSA)
   */
  async advanceStage(
    projectId: string,
    assessmentId: string,
    approvalNotes?: string
  ): Promise<SafetyAssessment> {
    const response = await api.post<{ data: SafetyAssessment }>(
      `${basePath(projectId)}/${assessmentId}/advance-stage`,
      { approvalNotes }
    );
    return response.data.data || response.data as unknown as SafetyAssessment;
  },

  // ==================== SFHA Work Products ====================

  /**
   * Get SFHA work product for an assessment
   * Returns null if no SFHA exists yet (expected for new assessments)
   */
  async getSFHA(projectId: string, assessmentId: string): Promise<SFHAWorkProduct | null> {
    const response = await api.get<{ data: SFHAWorkProduct | null }>(
      `${basePath(projectId)}/${assessmentId}/sfha`
    );
    return response.data.data ?? null;
  },

  /**
   * Create SFHA work product
   */
  async createSFHA(
    projectId: string,
    assessmentId: string,
    data: Partial<SFHAWorkProduct>
  ): Promise<SFHAWorkProduct> {
    const response = await api.post<{ data: SFHAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/sfha`,
      data
    );
    return response.data.data || response.data as unknown as SFHAWorkProduct;
  },

  /**
   * Update SFHA work product
   */
  async updateSFHA(
    projectId: string,
    assessmentId: string,
    data: Partial<SFHAWorkProduct>
  ): Promise<SFHAWorkProduct> {
    const response = await api.put<{ data: SFHAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/sfha`,
      data
    );
    return response.data.data || response.data as unknown as SFHAWorkProduct;
  },

  // ==================== FTA Work Products ====================

  /**
   * List FTA work products for an assessment
   */
  async listFTAs(projectId: string, assessmentId: string): Promise<FTAWorkProduct[]> {
    const response = await api.get<{ data: FTAWorkProduct[] }>(
      `${basePath(projectId)}/${assessmentId}/fta`
    );
    return response.data.data || response.data as unknown as FTAWorkProduct[];
  },

  /**
   * Get a specific FTA work product
   */
  async getFTA(projectId: string, assessmentId: string, ftaId: string): Promise<FTAWorkProduct> {
    const response = await api.get<{ data: FTAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/fta/${ftaId}`
    );
    return response.data.data || response.data as unknown as FTAWorkProduct;
  },

  /**
   * Create FTA work product
   */
  async createFTA(
    projectId: string,
    assessmentId: string,
    data: Partial<FTAWorkProduct>
  ): Promise<FTAWorkProduct> {
    const response = await api.post<{ data: FTAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/fta`,
      data
    );
    return response.data.data || response.data as unknown as FTAWorkProduct;
  },

  /**
   * Update FTA work product
   */
  async updateFTA(
    projectId: string,
    assessmentId: string,
    ftaId: string,
    data: Partial<FTAWorkProduct>
  ): Promise<FTAWorkProduct> {
    const response = await api.put<{ data: FTAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/fta/${ftaId}`,
      data
    );
    return response.data.data || response.data as unknown as FTAWorkProduct;
  },

  // ==================== FMEA Work Products ====================

  /**
   * Get FMEA work product for an assessment
   * Returns null if no FMEA exists yet (expected for new assessments)
   */
  async getFMEA(projectId: string, assessmentId: string): Promise<FMEAWorkProduct | null> {
    const response = await api.get<{ data: FMEAWorkProduct | null }>(
      `${basePath(projectId)}/${assessmentId}/fmea`
    );
    return response.data.data ?? null;
  },

  /**
   * Create FMEA work product
   */
  async createFMEA(
    projectId: string,
    assessmentId: string,
    data: Partial<FMEAWorkProduct>
  ): Promise<FMEAWorkProduct> {
    const response = await api.post<{ data: FMEAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/fmea`,
      data
    );
    return response.data.data || response.data as unknown as FMEAWorkProduct;
  },

  /**
   * Update FMEA work product
   */
  async updateFMEA(
    projectId: string,
    assessmentId: string,
    data: Partial<FMEAWorkProduct>
  ): Promise<FMEAWorkProduct> {
    const response = await api.put<{ data: FMEAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/fmea`,
      data
    );
    return response.data.data || response.data as unknown as FMEAWorkProduct;
  },

  // ==================== CMA Work Products ====================

  /**
   * Get CMA work product for an assessment
   * Returns null if no CMA exists yet (expected for new assessments)
   */
  async getCMA(projectId: string, assessmentId: string): Promise<CMAWorkProduct | null> {
    const response = await api.get<{ data: CMAWorkProduct | null }>(
      `${basePath(projectId)}/${assessmentId}/cma`
    );
    return response.data.data ?? null;
  },

  /**
   * Create CMA work product
   */
  async createCMA(
    projectId: string,
    assessmentId: string,
    data: Partial<CMAWorkProduct>
  ): Promise<CMAWorkProduct> {
    const response = await api.post<{ data: CMAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/cma`,
      data
    );
    return response.data.data || response.data as unknown as CMAWorkProduct;
  },

  /**
   * Update CMA work product
   */
  async updateCMA(
    projectId: string,
    assessmentId: string,
    data: Partial<CMAWorkProduct>
  ): Promise<CMAWorkProduct> {
    const response = await api.put<{ data: CMAWorkProduct }>(
      `${basePath(projectId)}/${assessmentId}/cma`,
      data
    );
    return response.data.data || response.data as unknown as CMAWorkProduct;
  },

  // ==================== Traceability ====================

  /**
   * Get traceability summary for an assessment
   */
  async getTraceabilitySummary(projectId: string, assessmentId: string): Promise<TraceabilitySummary> {
    const response = await api.get<{ data: TraceabilitySummary }>(
      `${basePath(projectId)}/${assessmentId}/traceability`
    );
    return response.data.data || response.data as unknown as TraceabilitySummary;
  },

  /**
   * Get traceability gaps
   */
  async getTraceabilityGaps(projectId: string, assessmentId: string): Promise<{
    unlinkedEvents: string[];
    andGatesWithoutCMA: string[];
    fmeaItemsWithoutLinks: string[];
    failureConditionsWithoutFTA: string[];
  }> {
    const response = await api.get<{ data: {
      unlinkedEvents: string[];
      andGatesWithoutCMA: string[];
      fmeaItemsWithoutLinks: string[];
      failureConditionsWithoutFTA: string[];
    }}>(`${basePath(projectId)}/${assessmentId}/gaps`);
    return response.data.data || response.data as unknown as {
      unlinkedEvents: string[];
      andGatesWithoutCMA: string[];
      fmeaItemsWithoutLinks: string[];
      failureConditionsWithoutFTA: string[];
    };
  },

  // ==================== Reports ====================

  /**
   * Generate SSA report
   */
  async generateReport(
    projectId: string,
    assessmentId: string,
    request: GenerateSSAReportRequest
  ): Promise<{ documentNumber: string }> {
    const response = await api.post<{ data: { documentNumber: string } }>(
      `${basePath(projectId)}/${assessmentId}/reports`,
      request
    );
    return response.data.data || response.data as unknown as { documentNumber: string };
  },

  /**
   * List generated reports for an assessment
   */
  async listReports(projectId: string, assessmentId: string): Promise<SafetyAssessment['reports']> {
    const assessment = await this.getAssessment(projectId, assessmentId);
    return assessment.reports;
  },
};
